import { configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query/react';
import { persistStore, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { combineReducers } from 'redux';

import clientReducer from '@/features/app/client/clientSlice';
import equipmentDataReducer from '@/features/app/equipment-data/equipmentDataSlice';
import equipmentPhotosReducer from '@/features/app/equipment-photos/equipmentPhotosSlice';
import receptionReducer from '@/features/app/reception/receptionSlice';
import receptionWizardReducer from '@/features/app/receptionWizardSlice';
import signatureConsentReducer from '@/features/app/signature-consent/signatureConsentSlice';
import rootAppShellReducer from './appShellSlice';
import authenticationReducer from '@/features/authentication/authenticationSlice';
import companyReducer from '@/features/company/companySlice';
import { ordersApi } from '@/services/reparoly/ordersApi';
import { catalogApi } from '@/services/reparoly/catalogApi';
import searchReducer from '@/store/slices/searchSlice';
import { paymentApi } from '@/services/reparoly/paymentApi';
import { legalTermsApi } from '@/services/reparoly/legalTermsApi';
import submissionReducer from '@/features/app/submission/submissionSlice';
import { clientsApi } from '@/services/reparoly/clientsApi';
import { intakesApi } from '@/services/reparoly/intakesApi';
import { receptionsApi } from '@/services/reparoly/receptionsApi';
import { authApi } from '@/services/reparoly/authApi';
import { onboardingApi } from '@/services/reparoly/onboardingApi';
import { dashboardApi } from '@/services/reparoly/dashboardApi';
import budgetReducer from '@/features/app/budget/budgetSlice';
import profileReducer from '@/features/profile/profileSlice';
import orderStatusesReducer from '@/features/dashboard/settings/order-states/orderStatusesSlice';
import paymentMethodsReducer from '@/features/dashboard/settings/payment-methods/paymentMethodsSlice';
import warrantiesReducer from '@/features/dashboard/warranties/warrantiesSlice';
import techniciansReducer from '@/features/users/techniciansSlice';
import allocationsDraftReducer from '@/features/app/parts/allocationsDraftSlice';
import orderTransitionReducer from '@/store/slices/orderTransitionSlice';
import { usersApi } from '@/services/reparoly/usersApi';
import { inventoryApi } from '@/services/reparoly/inventoryApi';
import { purchasesApi } from '@/services/reparoly/purchasesApi';
import { allocationsApi } from '@/services/reparoly/allocationsApi';
import { suppliersApi } from '@/services/reparoly/suppliersApi';
import { categoriesApi } from '@/services/reparoly/categoriesApi';
import { posApi } from '@/services/reparoly/posApi';
import { posTicketFooterApi } from '@/services/reparoly/posTicketFooterApi';
import suppliersReducer from '@/features/inventory/suppliers/suppliersSlice';
import categoriesReducer from '@/store/slices/categoriesSlice';
import selectedOrderReducer from '@/features/app/selected-order/selectedOrderSlice';
import receiptEditorReducer from '@/features/purchases/slices/receiptEditorSlice';
import posSaleReducer from '@/store/slices/posSaleSlice';

const persistConfigReparoly = {
  key: 'keydelivery',
  storage,
  whitelist: ['language'],
};

const persistConfigEquipPhotos = {
  key: 'equipmentPhotos',
  storage,
};

const persistConfigSignConsent = {
  key: 'signatureConsent',
  storage,
};

const persistConfigClient = {
  key: 'client',
  storage,
};

const persistConfigEquipData = {
  key: 'equipmentData',
  storage,
};

const persistConfigReception = {
  key: 'reception',
  storage,
};

const persistConfigReceptionWizard = {
  key: 'receptionWizard',
  storage,
};

const persistConfigBudget = {
  key: 'budget',
  storage,
};

const persistConfigCompany = {
  key: 'companyProfile',
  storage,
};

const persistConfigProfile = {
  key: 'profile',
  storage,
};

const persistConfigIsBiometrics = {
  key: 'isBiometrics',
  storage,
};

const persistConfigAuthentication = {
  key: 'authentication',
  storage,
  // No persistir credenciales sensibles ni flags de sesión
  blacklist: ['AccessToken', 'Password'],
};

// Persistencia del cache de RTK Query para catálogo
const persistConfigCatalogApi = {
  key: 'catalogApi',
  storage,
  // Puedes limitar qué ramas persistir con whitelist/blacklist si lo necesitas
  // whitelist: ['queries', 'provided'],
};

const persistConfigPaymentApi = {
  key: 'paymentApi',
  storage,
  // Puedes limitar qué ramas persistir con whitelist/blacklist si lo necesitas
  // whitelist: ['queries', 'provided'],
};

// Persistencia del cache de RTK Query para legal terms
const persistConfigLegalTermsApi = {
  key: 'legalTermsApi',
  storage,
  // Puedes limitar qué ramas persistir con whitelist/blacklist si lo necesitas
  // whitelist: ['queries', 'provided'],
};

// Persistencia de métodos de pago para mantener entre recargas
const persistConfigPaymentMethodsSlice = {
  key: 'paymentMethodsSlice',
  storage,
};

// Persistencia de técnicos para evitar re-fetch en navegación
const persistConfigTechniciansSlice = {
  key: 'technicians',
  storage,
};

const persistConfigSuppliersSlice = {
  key: 'suppliers',
  storage,
};

const persistConfigCategoriesSlice = {
  key: 'categories',
  storage,
};

// Persistencia de la orden seleccionada (para no perderla al refrescar)
const persistConfigSelectedOrder = {
  key: 'selectedOrder',
  storage,
};

const persistConfigPosSale = {
  key: 'posSale',
  storage,
  whitelist: [
    'terminalId',
    'terminalCode',
    'terminalName',
    'terminalStatus',
    'lastSaleId',
    'lastSaleSavedAt',
    'lastSaleTerminalId',
    'lastSaleCompanyId',
    'lastSaleBranchId',
  ],
};

// Nota: No persistimos caches de authApi ni onboardingApi.

const rootReducer = combineReducers({
  reparoly: persistReducer(persistConfigReparoly, rootAppShellReducer),
  equipmentPhotos: persistReducer(persistConfigEquipPhotos, equipmentPhotosReducer),
  signatureConsent: persistReducer(persistConfigSignConsent, signatureConsentReducer),
  client: persistReducer(persistConfigClient, clientReducer),
  equipmentData: persistReducer(persistConfigEquipData, equipmentDataReducer),
  reception: persistReducer(persistConfigReception, receptionReducer),
  receptionWizard: persistReducer(persistConfigReceptionWizard, receptionWizardReducer),
  authentication: persistReducer(persistConfigAuthentication, authenticationReducer),
  budget: persistReducer(persistConfigBudget, budgetReducer),
  companyProfile: persistReducer(persistConfigCompany, companyReducer),
  profile: persistReducer(persistConfigProfile, profileReducer),
  orderStatuses: orderStatusesReducer,
  paymentMethods: persistReducer(persistConfigPaymentMethodsSlice, paymentMethodsReducer),
  warranties: warrantiesReducer,
  technicians: persistReducer(persistConfigTechniciansSlice, techniciansReducer),
  suppliers: persistReducer(persistConfigSuppliersSlice, suppliersReducer),
  categories: persistReducer(persistConfigCategoriesSlice, categoriesReducer),
  selectedOrder: persistReducer(persistConfigSelectedOrder, selectedOrderReducer),
  allocationsDraft: allocationsDraftReducer,
  search: searchReducer,
  submission: submissionReducer,
  orderTransition: orderTransitionReducer,
  // UI state for purchases receipts editor
  receiptEditor: receiptEditorReducer,
  // POS fast sale
  posSale: persistReducer(persistConfigPosSale, posSaleReducer),
  // Reducer del API de órdenes
  [ordersApi.reducerPath]: ordersApi.reducer,
  // Reducer del API de catálogo (persistido para cache entre recargas)
  [catalogApi.reducerPath]: persistReducer(persistConfigCatalogApi, catalogApi.reducer),
  [paymentApi.reducerPath]: persistReducer(persistConfigPaymentApi, paymentApi.reducer),
  [legalTermsApi.reducerPath]: persistReducer(persistConfigLegalTermsApi, legalTermsApi.reducer),
  [clientsApi.reducerPath]: clientsApi.reducer,
  [intakesApi.reducerPath]: intakesApi.reducer,
  [receptionsApi.reducerPath]: receptionsApi.reducer,
  [authApi.reducerPath]: authApi.reducer,
  [onboardingApi.reducerPath]: onboardingApi.reducer,
  [dashboardApi.reducerPath]: dashboardApi.reducer,
  [usersApi.reducerPath]: usersApi.reducer,
  [inventoryApi.reducerPath]: inventoryApi.reducer,
  [purchasesApi.reducerPath]: purchasesApi.reducer,
  [allocationsApi.reducerPath]: allocationsApi.reducer,
  [suppliersApi.reducerPath]: suppliersApi.reducer,
  [categoriesApi.reducerPath]: categoriesApi.reducer,
  [posApi.reducerPath]: posApi.reducer,
  [posTicketFooterApi.reducerPath]: posTicketFooterApi.reducer,
});

export type RootState = ReturnType<typeof rootReducer>;

export function makeStore() {
  const store = configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [
            'persist/PERSIST',
            'persist/FLUSH',
            'persist/PURGE',
            'purchasesApi/executeMutation/pending',
            'purchasesApi/executeMutation/fulfilled',
            'purchasesApi/executeMutation/rejected',
          ],
          ignoredPaths: [`${purchasesApi.reducerPath}.mutations`],
        },
      }).concat(
        // Middlewares de APIs
        ordersApi.middleware,
        catalogApi.middleware,
        paymentApi.middleware,
        legalTermsApi.middleware,
        clientsApi.middleware,
        intakesApi.middleware,
        receptionsApi.middleware,
        authApi.middleware,
        onboardingApi.middleware,
        dashboardApi.middleware,
        usersApi.middleware,
        inventoryApi.middleware,
        purchasesApi.middleware,
        allocationsApi.middleware,
        suppliersApi.middleware,
        categoriesApi.middleware,
        posApi.middleware,
        posTicketFooterApi.middleware,
      ),
  });

  const persistor = persistStore(store);
  setupListeners(store.dispatch);

  return { store, persistor };
}

export type AppStore = ReturnType<typeof makeStore>['store'];
export type AppDispatch = AppStore['dispatch'];
